import { mediaTranscriptPaths } from "@circle-react/helpers/urlHelpers";
import type { MediaTranscript } from "@circle-react/types/MediaTranscript";
import { useMemo } from "react";
import { useQuery } from "react-query";
import type { VTTData } from "webvtt-parser";
import { WebVTTParser } from "webvtt-parser";

const unescapeHtmlEntities = (text: string): string =>
  text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

export const useParsedWebVTTFile = (mediaTranscript?: MediaTranscript) => {
  const {
    id: mediaTranscriptId,
    enabled: isEnabled,
    is_ready: isReady,
  } = mediaTranscript || {};

  const { data, isError } = useQuery({
    queryKey: ["mediaTranscript", "webvtt", mediaTranscriptId],

    queryFn: () =>
      fetch(
        mediaTranscriptPaths.showVtt({
          id: mediaTranscriptId?.toString() ?? "",
        }),
      ).then(res => res.text()),

    enabled: !!mediaTranscriptId && isReady && isEnabled,
  });
  const parsedWebvtt: VTTData | null = useMemo(() => {
    if (!data || isError) {
      return null;
    }
    const parser = new WebVTTParser();
    const parsed = parser.parse(data);

    // Unescape HTML entities in cue text
    if (parsed?.cues) {
      parsed.cues = parsed.cues.map(cue => ({
        ...cue,
        text: unescapeHtmlEntities(cue.text),
      }));
    }

    return parsed;
  }, [data, isError]);

  return {
    webvtt: data,
    parsedWebvtt,
  };
};
